import Phaser from 'phaser';
import { Entity, Allegiance } from '../entities/base/Entity';
import { Projectile } from '../projectiles/Projectile';
import { DataManagerImagePoint } from '../systems/DataManagerImagePoint';
import { HealthBehavior } from '../entities/behaviors/HealthBehavior';
import { LayerDepths } from '../constants/enums';

/**
 * CollisionDebugOverlay - Visualiza las cajas de colisión de todos los objetos
 *
 * Toggle con SHIFT+F1 para mostrar/ocultar las hitboxes de:
 * - Player (verde)
 * - Entidades neutrales (amarillo)
 * - Entidades enemigas (rojo)
 * - Proyectiles (cyan)
 * - Otros objetos físicos (blanco)
 */

const COLORS = {
  player: 0x00ff00,       // Verde
  neutral: 0xffff00,      // Amarillo
  enemy: 0xff0000,        // Rojo
  projectile: 0x00ffff,   // Cyan
  other: 0xffffff,        // Blanco
  ground: 0x664422,       // Marrón oscuro para el suelo
  imagePoint: 0xff8800,   // Naranja para image points
  text: '#00ff00',
};

export class CollisionDebugOverlay {
  private scene: Phaser.Scene;
  private graphics: Phaser.GameObjects.Graphics;
  private enabled: boolean = false;
  private imagePointsEnabled: boolean = false; // Separate toggle for image points (SHIFT+F6)
  private destroyed: boolean = false;
  private f1Key: Phaser.Input.Keyboard.Key;
  private statusText: Phaser.GameObjects.Text;
  private tooltipText: Phaser.GameObjects.Text;
  private labelPool: Phaser.GameObjects.Text[] = [];
  private activeLabelCount: number = 0;

  // Image point labels (smaller)
  private imagePointLabelPool: Phaser.GameObjects.Text[] = [];
  private activeImagePointLabelCount: number = 0;

  // Referencia al player para colorear diferente
  private player?: Phaser.Physics.Arcade.Sprite;

  // Entidad seleccionada para mostrar image points (click para cambiar)
  private selectedEntity?: Phaser.Physics.Arcade.Sprite;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Crear graphics para dibujar hitboxes
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(LayerDepths.DebugText);
    this.graphics.setVisible(false);

    // Crear texto de estado (más grande y visible)
    this.statusText = scene.add.text(16, scene.scale.height - 110, '', {
      fontFamily: 'monospace',
      fontSize: '21px',
      color: COLORS.text,
      backgroundColor: '#000000cc',
      padding: { x: 12, y: 8 },
    });
    this.statusText.setScrollFactor(0);
    this.statusText.setDepth(LayerDepths.DebugOverlay);
    this.statusText.setVisible(false);

    // Crear tooltip para hover - más legible
    this.tooltipText = scene.add.text(0, 0, '', {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#00ff00',
      backgroundColor: '#111111',
      padding: { x: 12, y: 10 },
      stroke: '#000000',
      strokeThickness: 2,
    });
    this.tooltipText.setDepth(LayerDepths.DebugTooltip);
    this.tooltipText.setVisible(false);

    // Registrar tecla SHIFT+F1
    this.f1Key = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.F1);

    // Escuchar evento de toggle (SHIFT+F1)
    scene.input.keyboard!.on('keydown-F1', (event: KeyboardEvent) => {
      if (event.shiftKey) {
        event.preventDefault();
        this.toggle();
      }
    });

    // Escuchar evento de toggle image points (SHIFT+F6)
    scene.input.keyboard!.on('keydown-F6', (event: KeyboardEvent) => {
      if (event.shiftKey) {
        event.preventDefault();
        this.toggleImagePoints();
      }
    });

    // Escuchar click para seleccionar entidades
    scene.input.on('pointerdown', this.onPointerDown, this);
  }

  /**
   * Maneja click para seleccionar entidad
   */
  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (!this.enabled && !this.imagePointsEnabled) return;

    const worldPoint = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);

    // Buscar entidad bajo el click
    let foundEntity: Phaser.Physics.Arcade.Sprite | undefined;

    this.scene.physics.world.bodies.entries.forEach((body: Phaser.Physics.Arcade.Body) => {
      if (!body.enable || !body.gameObject) return;

      if (
        worldPoint.x >= body.x &&
        worldPoint.x <= body.x + body.width &&
        worldPoint.y >= body.y &&
        worldPoint.y <= body.y + body.height
      ) {
        const sprite = body.gameObject as Phaser.Physics.Arcade.Sprite;
        if (sprite.texture) {
          foundEntity = sprite;
        }
      }
    });

    if (foundEntity) {
      this.selectedEntity = foundEntity;
      this.logSelectedEntityInfo(foundEntity);
    }
  }

  /**
   * Muestra información detallada de la entidad seleccionada en consola
   */
  private logSelectedEntityInfo(sprite: Phaser.Physics.Arcade.Sprite): void {
    const name = sprite.texture?.key || 'Unknown';
    const body = sprite.body as Phaser.Physics.Arcade.Body;

    // Info básica
    const entityId = (sprite as unknown as Entity).entityId || 'N/A';
    const allegiance = (sprite as unknown as Entity).allegiance || 'N/A';

    // Info de animación
    const animInfo = this.getAnimationInfo(sprite);

    // Info de HP (si es Entity con HealthBehavior)
    let hpInfo = '';
    if (sprite instanceof Entity) {
      const healthBehavior = sprite.getBehavior(HealthBehavior);
      if (healthBehavior) {
        hpInfo = `HP: ${healthBehavior.hp}/${healthBehavior.maxHp}`;
        if (healthBehavior.useProRate && healthBehavior.currentDamageFrame !== undefined) {
          hpInfo += ` (damageFrame: ${healthBehavior.currentDamageFrame})`;
        }
      }
    }

    // Construir log
    console.log(`%c[Debug] Selected: ${name}`, 'color: #00ff00; font-weight: bold');
    console.log(`  ID: ${entityId}`);
    console.log(`  Allegiance: ${allegiance}`);
    console.log(`  Position: (${Math.round(sprite.x)}, ${Math.round(sprite.y)})`);
    if (body) {
      console.log(`  Body: (${Math.round(body.x)}, ${Math.round(body.y)}) ${Math.round(body.width)}x${Math.round(body.height)}`);
    }
    if (animInfo) {
      console.log(`  Animation: ${animInfo.animKey} [${animInfo.frameIndex}]`);
      console.log(`  Frame: ${animInfo.frameName}`);
      console.log(`  Atlas position: ${animInfo.atlasPos}`);
    }
    if (hpInfo) {
      console.log(`  ${hpInfo}`);
    }
  }

  /**
   * Establece referencia al player para colorear diferente
   */
  setPlayer(player: Phaser.Physics.Arcade.Sprite): void {
    this.player = player;
  }

  /**
   * Toggle visibilidad del overlay (collision boxes)
   */
  toggle(): void {
    this.enabled = !this.enabled;
    this.graphics.setVisible(this.enabled || this.imagePointsEnabled);
    this.statusText.setVisible(this.enabled);

    if (this.enabled) {
      console.log('[Debug] Collision overlay ENABLED - Press SHIFT+F1 to hide');
      // Select player by default when enabling
      this.selectedEntity = this.player;
    } else {
      console.log('[Debug] Collision overlay DISABLED');
      if (!this.imagePointsEnabled) {
        this.graphics.clear();
      }
      this.tooltipText.setVisible(false);
      this.hideAllLabels();
    }
  }

  /**
   * Toggle visibilidad de image points (SHIFT+F6)
   */
  toggleImagePoints(): void {
    this.imagePointsEnabled = !this.imagePointsEnabled;
    this.graphics.setVisible(this.enabled || this.imagePointsEnabled);

    if (this.imagePointsEnabled) {
      console.log('[Debug] Image points ENABLED - Press SHIFT+F6 to hide');
      // Select player by default when enabling
      if (!this.selectedEntity) {
        this.selectedEntity = this.player;
      }
    } else {
      console.log('[Debug] Image points DISABLED');
      // Hide image point UI
      if (this.imagePointLegend) {
        this.imagePointLegend.setVisible(false);
      }
      if (this.imagePointHoverLine) {
        this.imagePointHoverLine.clear();
      }
      // Hide image point labels
      for (const label of this.imagePointLabelPool) {
        label.setVisible(false);
      }
      this.activeImagePointLabelCount = 0;
      // Clear graphics if collision debug is also off
      if (!this.enabled) {
        this.graphics.clear();
      }
    }
  }

  /**
   * Determina el color según el tipo de objeto
   */
  private getColor(obj: Phaser.GameObjects.GameObject): number {
    // Es el player?
    if (this.player && obj === this.player) {
      return COLORS.player;
    }

    // Projectiles (verificar ANTES de Entity porque Projectile extends Entity)
    if (obj instanceof Projectile) {
      return COLORS.projectile;
    }

    // Es una Entity?
    if (obj instanceof Entity) {
      switch (obj.allegiance) {
        case Allegiance.Player:
          return COLORS.player;
        case Allegiance.Enemy:
          return COLORS.enemy;
        case Allegiance.Neutral:
        default:
          return COLORS.neutral;
      }
    }

    return COLORS.other;
  }

  /**
   * Dibuja un hitbox (Arcade body)
   */
  private drawArcadeBody(body: Phaser.Physics.Arcade.Body, color: number, label?: string): void {
    const gameObject = body.gameObject;

    // Para proyectiles con collisionType: 'line', dibujar línea en lugar de rectángulo
    if (gameObject instanceof Projectile) {
      const collisionType = gameObject.definition.collisionType;
      if (collisionType === 'line') {
        this.drawLineCollision(gameObject, color);
        return;
      }
    }

    const alpha = 0.4;
    const lineWidth = 3;

    // Fondo semi-transparente
    this.graphics.fillStyle(color, alpha);
    this.graphics.fillRect(body.x, body.y, body.width, body.height);

    // Borde sólido más grueso
    this.graphics.lineStyle(lineWidth, color, 1);
    this.graphics.strokeRect(body.x, body.y, body.width, body.height);

    // Origin del sprite (cruz) - el x/y del sprite ES donde está el origin
    const sprite = body.gameObject as Phaser.GameObjects.Sprite;
    const originX = sprite?.x ?? body.x + body.width / 2;
    const originY = sprite?.y ?? body.y + body.height / 2;
    const crossSize = Math.min(body.width, body.height) * 0.3;

    this.graphics.lineStyle(2, 0xff00ff, 1); // Magenta para distinguir del body
    this.graphics.beginPath();
    this.graphics.moveTo(originX - crossSize, originY);
    this.graphics.lineTo(originX + crossSize, originY);
    this.graphics.moveTo(originX, originY - crossSize);
    this.graphics.lineTo(originX, originY + crossSize);
    this.graphics.strokePath();
  }

  /**
   * Dibuja la colisión para proyectiles con collisionType: 'line'
   * Muestra el AABB real del body (16x16 en la punta) y una línea visual tenue como referencia
   */
  private drawLineCollision(projectile: Projectile, color: number): void {
    const body = projectile.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    // 1. Dibujar el AABB real del body (16x16 en la punta)
    const alpha = 0.4;
    const lineWidth = 3;

    this.graphics.fillStyle(color, alpha);
    this.graphics.fillRect(body.x, body.y, body.width, body.height);

    this.graphics.lineStyle(lineWidth, color, 1);
    this.graphics.strokeRect(body.x, body.y, body.width, body.height);

    // 2. Dibujar línea visual tenue como referencia (solo visual, no es la colisión)
    const angle = projectile.rotation;
    const length = projectile.displayWidth;
    const originX = projectile.originX;

    const startOffset = -originX * length;
    const endOffset = (1 - originX) * length;

    const x1 = projectile.x + Math.cos(angle) * startOffset;
    const y1 = projectile.y + Math.sin(angle) * startOffset;
    const x2 = projectile.x + Math.cos(angle) * endOffset;
    const y2 = projectile.y + Math.sin(angle) * endOffset;

    this.graphics.lineStyle(2, color, 0.3);
    this.graphics.beginPath();
    this.graphics.moveTo(x1, y1);
    this.graphics.lineTo(x2, y2);
    this.graphics.strokePath();

    // 3. Cruz en el origen (magenta)
    const crossSize = 8;
    this.graphics.lineStyle(2, 0xff00ff, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(projectile.x - crossSize, projectile.y);
    this.graphics.lineTo(projectile.x + crossSize, projectile.y);
    this.graphics.moveTo(projectile.x, projectile.y - crossSize);
    this.graphics.lineTo(projectile.x, projectile.y + crossSize);
    this.graphics.strokePath();
  }

  /**
   * Obtiene la etiqueta para un objeto
   */
  private getLabel(obj: Phaser.GameObjects.GameObject): string {
    // Es el player?
    if (this.player && obj === this.player) {
      return 'Player';
    }

    // Projectiles (verificar ANTES de Entity porque Projectile extends Entity)
    if (obj instanceof Projectile) {
      const sprite = obj as Phaser.Physics.Arcade.Sprite;
      return sprite.texture?.key || 'Projectile';
    }

    // Es una Entity del nivel (con ID)?
    if (obj instanceof Entity) {
      const sprite = obj as Phaser.Physics.Arcade.Sprite;
      const texture = sprite.texture?.key || obj.constructor.name;
      const id = obj.entityId || 'N/A';
      return `${texture} [${id}]`;  // "cow1 [#5]"
    }

    // Otro objeto
    const sprite = obj as Phaser.Physics.Arcade.Sprite;
    return sprite.texture?.key || obj.constructor.name;
  }

  /**
   * Obtiene o crea un texto del pool para etiquetas
   */
  private getLabelText(index: number): Phaser.GameObjects.Text {
    if (index < this.labelPool.length) {
      return this.labelPool[index];
    }

    // Crear nuevo texto - más grande y legible
    const text = this.scene.add.text(0, 0, '', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '27px',
      fontStyle: 'bold',
      color: '#ffffff',
      backgroundColor: '#000000',
      padding: { x: 6, y: 3 },
      stroke: '#000000',
      strokeThickness: 2,
    });
    text.setDepth(LayerDepths.DebugGraphics);
    this.labelPool.push(text);
    return text;
  }

  /**
   * Oculta todas las etiquetas del pool
   */
  private hideAllLabels(): void {
    for (const label of this.labelPool) {
      label.setVisible(false);
    }
    this.activeLabelCount = 0;
    // Also hide image point labels
    for (const label of this.imagePointLabelPool) {
      label.setVisible(false);
    }
    this.activeImagePointLabelCount = 0;
  }

  // Store image points data for hover detection
  private currentImagePoints: Array<{ name: string; x: number; y: number; letter: string }> = [];
  private imagePointLegend!: Phaser.GameObjects.Text;
  private imagePointHoverLine!: Phaser.GameObjects.Graphics;

  /**
   * Obtiene o crea un texto pequeño para image point labels
   */
  private getImagePointLabelText(index: number): Phaser.GameObjects.Text {
    if (index < this.imagePointLabelPool.length) {
      return this.imagePointLabelPool[index];
    }

    // Crear nuevo texto con letra negra sobre fondo naranja
    const text = this.scene.add.text(0, 0, '', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#000000',
      backgroundColor: '#ff8800',
      padding: { x: 3, y: 1 },
    });
    text.setDepth(LayerDepths.DebugOverlay);
    this.imagePointLabelPool.push(text);
    return text;
  }

  /**
   * Inicializa los elementos de UI para image points
   */
  private ensureImagePointUI(): void {
    if (!this.imagePointLegend) {
      this.imagePointLegend = this.scene.add.text(16, 16, '', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#ff8800',
        backgroundColor: '#000000dd',
        padding: { x: 8, y: 6 },
      });
      this.imagePointLegend.setScrollFactor(0);
      this.imagePointLegend.setDepth(LayerDepths.DebugTooltip);
      this.imagePointLegend.setVisible(false);
    }
    if (!this.imagePointHoverLine) {
      this.imagePointHoverLine = this.scene.add.graphics();
      this.imagePointHoverLine.setDepth(LayerDepths.DebugGraphics);
    }
  }

  /**
   * Dibuja los image points de la entidad seleccionada
   */
  private drawImagePoints(): void {
    // Hide old image point labels first
    for (const label of this.imagePointLabelPool) {
      label.setVisible(false);
    }
    this.activeImagePointLabelCount = 0;

    // Use selected entity or fall back to player
    const targetEntity = this.selectedEntity || this.player;
    if (!targetEntity || !targetEntity.active) {
      // If selected entity is gone, fall back to player
      if (this.selectedEntity && !this.selectedEntity.active) {
        this.selectedEntity = this.player;
      }
      if (!this.player) return;
    }

    const sprite = targetEntity as Phaser.Physics.Arcade.Sprite;
    const imagePointManager = this.scene.registry.get('imagePointManager') as DataManagerImagePoint | undefined;
    if (!imagePointManager) return;

    this.ensureImagePointUI();
    this.currentImagePoints = [];

    // Get entity's current animation info
    const entity = sprite as unknown as Entity;
    const animName = (entity as any).currentAnimName || 'default';
    const frameIndex = (entity as any).currentFrameIndex || 0;
    const entityName = sprite.texture?.key || 'Unknown';

    // Get all image points
    const textureKey = sprite.texture.key;
    const points = imagePointManager.getAllWorldPositions(
      textureKey,
      animName,
      frameIndex,
      sprite.x,
      sprite.y,
      sprite.displayWidth,
      sprite.displayHeight,
      sprite.originX,
      sprite.originY
    );


    // Letters for labeling
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

    // Draw ORIGIN point first (where sprite.x, sprite.y is) - marked with "+"
    const originX = sprite.x;
    const originY = sprite.y;
    const crossSize = 12;

    // Draw "+" for origin in magenta
    this.graphics.lineStyle(3, 0xff00ff, 1); // Magenta
    this.graphics.beginPath();
    this.graphics.moveTo(originX - crossSize, originY);
    this.graphics.lineTo(originX + crossSize, originY);
    this.graphics.moveTo(originX, originY - crossSize);
    this.graphics.lineTo(originX, originY + crossSize);
    this.graphics.strokePath();

    // Draw "O" label for origin
    const originLabel = this.getImagePointLabelText(this.activeImagePointLabelCount);
    originLabel.setText('O');
    originLabel.setStyle({ backgroundColor: '#ff00ff', color: '#ffffff' });
    originLabel.setPosition(originX + 10, originY - 16);
    originLabel.setVisible(true);
    this.activeImagePointLabelCount++;

    // Draw each image point with letter
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const letter = letters[i] || '?';

      // Store for hover detection
      this.currentImagePoints.push({ ...point, letter });

      // Draw filled circle
      this.graphics.fillStyle(COLORS.imagePoint, 1);
      this.graphics.fillCircle(point.x, point.y, 6);

      // Draw border
      this.graphics.lineStyle(2, 0x000000, 1);
      this.graphics.strokeCircle(point.x, point.y, 6);

      // Draw letter label offset to the side
      const label = this.getImagePointLabelText(this.activeImagePointLabelCount);
      label.setText(letter);
      label.setStyle({ backgroundColor: '#ff8800', color: '#000000' });
      label.setPosition(point.x + 8, point.y - 8);
      label.setVisible(true);
      this.activeImagePointLabelCount++;
    }

    // Update hover detection for image points
    this.updateImagePointHover();
  }

  /**
   * Detecta hover sobre image points y muestra leyenda
   */
  private updateImagePointHover(): void {
    if (!this.imagePointLegend || !this.imagePointHoverLine) return;

    const pointer = this.scene.input.activePointer;
    const worldPoint = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);

    this.imagePointHoverLine.clear();

    // Find if hovering over any image point (within 15px radius)
    let hoveredPoint: { name: string; x: number; y: number; letter: string } | null = null;
    let minDist = 15;

    for (const point of this.currentImagePoints) {
      const dist = Math.sqrt(
        Math.pow(worldPoint.x - point.x, 2) + Math.pow(worldPoint.y - point.y, 2)
      );
      if (dist < minDist) {
        minDist = dist;
        hoveredPoint = point;
      }
    }

    // Get selected entity info
    const targetEntity = this.selectedEntity || this.player;
    const entityName = targetEntity?.texture?.key || 'Unknown';
    const sprite = targetEntity as Phaser.Physics.Arcade.Sprite;

    // Get animation info for header
    let animHeader = '';
    if (targetEntity) {
      const animInfo = this.getAnimationInfo(targetEntity as Phaser.Physics.Arcade.Sprite);
      if (animInfo) {
        animHeader = `\nAnim: ${animInfo.animKey} [${animInfo.frameIndex}]\nFrame: ${animInfo.frameName}\nAtlas: ${animInfo.atlasPos}`;
      }
    }

    // Origin info
    const originInfo = sprite
      ? `\n\nORIGIN (magenta +):\nPos: (${Math.round(sprite.x)}, ${Math.round(sprite.y)})\nOrigin: (${sprite.originX.toFixed(2)}, ${sprite.originY.toFixed(2)})`
      : '';

    const header = `SELECTED [${entityName}]${animHeader}${originInfo}\n\nIMAGE POINTS (orange):`;

    if (hoveredPoint) {
      // Show legend with all points, highlighting the hovered one
      const legendLines = this.currentImagePoints.map(p => {
        const prefix = p.letter === hoveredPoint!.letter ? '> ' : '  ';
        return `${prefix}${p.letter}: ${p.name}`;
      });
      this.imagePointLegend.setText(header + '\n' + legendLines.join('\n') + '\n\nClick entity to select');
      this.imagePointLegend.setVisible(true);

      // Draw line from legend to the hovered point
      this.imagePointHoverLine.lineStyle(2, COLORS.imagePoint, 0.8);
      this.imagePointHoverLine.beginPath();
      this.imagePointHoverLine.moveTo(this.imagePointLegend.x + this.imagePointLegend.width + 5,
                                       this.imagePointLegend.y + this.imagePointLegend.height / 2);
      this.imagePointHoverLine.lineTo(hoveredPoint.x, hoveredPoint.y);
      this.imagePointHoverLine.strokePath();

      // Draw highlight circle on hovered point
      this.imagePointHoverLine.lineStyle(3, 0xffffff, 1);
      this.imagePointHoverLine.strokeCircle(hoveredPoint.x, hoveredPoint.y, 10);
    } else {
      // Show compact legend
      const legendLines = this.currentImagePoints.map(p => `${p.letter}: ${p.name}`);
      const noPoints = this.currentImagePoints.length === 0 ? '(none)' : '';
      this.imagePointLegend.setText(header + '\n' + legendLines.join('\n') + noPoints + '\n\nClick entity to select');
      this.imagePointLegend.setVisible(true);
    }
  }

  /**
   * Muestra una etiqueta sobre una posición
   * For wide bodies like ground TileSprites, centers label in visible area
   */
  private showLabel(index: number, text: string, x: number, y: number, width?: number): void {
    const label = this.getLabelText(index);
    label.setText(text);

    // If width is provided (for static bodies), center label in visible portion
    let labelX = x;
    if (width !== undefined && width > this.scene.scale.width) {
      // For very wide bodies (like ground), center in camera view
      const camera = this.scene.cameras.main;
      labelX = Math.max(camera.scrollX + 10, x);
      labelX = Math.min(labelX, camera.scrollX + camera.width - label.width - 10);
    }

    label.setPosition(labelX, y - 32);
    label.setVisible(true);
    this.activeLabelCount++;
  }

  /**
   * Dibuja un static body (Arcade)
   */
  private drawStaticBody(body: Phaser.Physics.Arcade.StaticBody, color: number): void {
    const alpha = 0.5; // Más opaco para mejor visibilidad
    const lineWidth = 3;

    this.graphics.fillStyle(color, alpha);
    this.graphics.fillRect(body.x, body.y, body.width, body.height);

    this.graphics.lineStyle(lineWidth, color, 1);
    this.graphics.strokeRect(body.x, body.y, body.width, body.height);
  }

  /**
   * Update - llamar cada frame para redibujar
   */
  update(): void {
    if (this.destroyed) return;
    if (!this.enabled && !this.imagePointsEnabled) return;

    // Limpiar gráficos anteriores
    this.graphics.clear();

    // Only draw collision debug if enabled
    if (this.enabled) {
      this.hideAllLabels();

      let entityCount = 0;
      let projectileCount = 0;
      let otherCount = 0;
      let playerDrawn = false;
      let labelIndex = 0;

      // Obtener todos los cuerpos físicos
      const bodies = this.scene.physics.world.bodies;
      const staticBodies = this.scene.physics.world.staticBodies;

      // Dibujar cuerpos dinámicos
      bodies.entries.forEach((body: Phaser.Physics.Arcade.Body) => {
        const gameObject = body.gameObject;
        if (!gameObject || !body.enable) return;

        const color = this.getColor(gameObject);
        const label = this.getLabel(gameObject);
        this.drawArcadeBody(body, color, label);
        this.showLabel(labelIndex++, label, body.x, body.y);

        // Contar por tipo (Projectile ANTES de Entity porque Projectile extends Entity)
        if (this.player && gameObject === this.player) {
          playerDrawn = true;
          otherCount++; // Player cuenta como other para el total
        } else if (gameObject instanceof Projectile) {
          projectileCount++;
        } else if (gameObject instanceof Entity) {
          entityCount++;
          // Debug: verificar si debería ser un proyectil
          const constructorName = (gameObject.constructor as { name?: string }).name;
          if (constructorName === 'Projectile') {
            console.warn('[Debug] Found Projectile by name but not instanceof!', gameObject);
          }
        } else {
          otherCount++;
        }
      });

      // Dibujar player explícitamente si no fue dibujado
      if (this.player && !playerDrawn) {
        const playerBody = this.player.body as Phaser.Physics.Arcade.Body;
        if (playerBody && playerBody.enable) {
          this.drawArcadeBody(playerBody, COLORS.player, 'Player');
          this.showLabel(labelIndex++, 'Player', playerBody.x, playerBody.y);
        }
      }

      // Dibujar cuerpos estáticos (solidEntities: TileSprites, solid entities)
      staticBodies.entries.forEach((body: Phaser.Physics.Arcade.StaticBody) => {
        if (!body.enable) return;

        // Use ground color for static bodies (now includes TileSprites with solid behavior)
        this.drawStaticBody(body, COLORS.ground);
        this.showLabel(labelIndex++, 'Solid', body.x, body.y, body.width);
        otherCount++;
      });

      // Actualizar texto de estado
      const playerBody = this.player?.body as Phaser.Physics.Arcade.Body | undefined;
      const playerInfo = playerBody
        ? `Player: pos(${Math.round(playerBody.x)}, ${Math.round(playerBody.y)}) size(${Math.round(playerBody.width)}x${Math.round(playerBody.height)})`
        : 'Player: N/A';

      // Contar assets
      const assetStats = this.getAssetStats();

      // Safety check - statusText may be destroyed during scene transitions
      if (!this.statusText?.active) return;

      this.statusText.setText(
        `[COLLISION DEBUG] SHIFT+F1 to hide\n` +
        `Entities: ${entityCount} | Projectiles: ${projectileCount} | Other: ${otherCount}\n` +
        `${playerInfo}\n` +
        `Assets: ${assetStats}`
      );

      // Detectar hover del mouse sobre entidades
      this.updateHover();
    }

    // Draw image points independently (SHIFT+F6)
    if (this.imagePointsEnabled) {
      this.drawImagePoints();
    }
  }

  /**
   * Detecta si el mouse está sobre alguna entidad y muestra tooltip
   */
  private updateHover(): void {
    const pointer = this.scene.input.activePointer;
    const worldPoint = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);

    let foundBody: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody | null = null;
    let foundObject: Phaser.GameObjects.GameObject | null = null;

    // Buscar body dinámico bajo el cursor
    this.scene.physics.world.bodies.entries.forEach((body: Phaser.Physics.Arcade.Body) => {
      if (!body.enable || !body.gameObject) return;

      // Verificar si el punto está dentro del body
      if (
        worldPoint.x >= body.x &&
        worldPoint.x <= body.x + body.width &&
        worldPoint.y >= body.y &&
        worldPoint.y <= body.y + body.height
      ) {
        foundBody = body;
        foundObject = body.gameObject;
      }
    });

    // Buscar body estático bajo el cursor (TileSprites sólidos, etc.)
    if (!foundBody) {
      this.scene.physics.world.staticBodies.entries.forEach((body: Phaser.Physics.Arcade.StaticBody) => {
        if (!body.enable || !body.gameObject) return;

        // Verificar si el punto está dentro del body
        if (
          worldPoint.x >= body.x &&
          worldPoint.x <= body.x + body.width &&
          worldPoint.y >= body.y &&
          worldPoint.y <= body.y + body.height
        ) {
          foundBody = body;
          foundObject = body.gameObject;
        }
      });
    }

    if (foundBody && foundObject) {
      this.showTooltip(foundBody as Phaser.Physics.Arcade.Body, foundObject, pointer.x, pointer.y);
    } else {
      this.tooltipText.setVisible(false);
    }
  }

  /**
   * Get behavior names from an Entity
   */
  private getBehaviorTags(entity: Entity): string[] {
    const tags: string[] = [];
    for (const behavior of entity.behaviors) {
      // Extract behavior name from constructor (e.g., "WalkerBehavior" -> "walker")
      const name = behavior.constructor.name
        .replace('Behavior', '')
        .toLowerCase();
      tags.push(name);
    }
    return tags;
  }

  /**
   * Muestra tooltip con info de la entidad
   */
  private showTooltip(
    body: Phaser.Physics.Arcade.Body | Phaser.Physics.Arcade.StaticBody,
    obj: Phaser.GameObjects.GameObject,
    screenX: number,
    screenY: number
  ): void {
    const sprite = obj as Phaser.Physics.Arcade.Sprite | Phaser.GameObjects.TileSprite;

    // Obtener textura
    const textureName = (sprite as { texture?: { key?: string } }).texture?.key || 'N/A';

    // Check if it's a static body
    const isStatic = body instanceof Phaser.Physics.Arcade.StaticBody;

    const lines: string[] = [];

    // === PLAYER ===
    if (this.player && obj === this.player) {
      lines.push(`[PLAYER] player`);
      lines.push(`Type: ENTITY`);

      const p = this.player as unknown as { health?: number; maxHealth?: number };
      if (p.health !== undefined) {
        lines.push(`HP: ${p.health}/${p.maxHealth}`);
      }

      // Get player behaviors
      const playerEntity = this.player as unknown as Entity;
      if (playerEntity.behaviors) {
        const tags = this.getBehaviorTags(playerEntity);
        if (tags.length > 0) {
          lines.push(`Behaviors: [${tags.join('] [')}]`);
        }
      }
    }
    // === ENTITY ===
    else if (obj instanceof Entity) {
      const entityName = (obj as Entity & { prefabName?: string }).prefabName || textureName;
      lines.push(`[${entityName.toUpperCase()}] ${obj.entityId || ''}`);
      lines.push(`Type: ENTITY`);
      lines.push(`Allegiance: ${obj.allegiance}`);

      // Get behaviors as tags
      const tags = this.getBehaviorTags(obj);
      if (tags.length > 0) {
        lines.push(`Behaviors: [${tags.join('] [')}]`);
      }

      // HP if has HealthBehavior
      const healthBehavior = obj.getBehavior(HealthBehavior);
      if (healthBehavior) {
        lines.push(`HP: ${healthBehavior.hp}/${healthBehavior.maxHp}`);
      }

      // State info
      if (obj.state && Object.keys(obj.state).length > 0) {
        const stateStr = Object.entries(obj.state)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => `${k}=${v}`)
          .join(', ');
        if (stateStr) {
          lines.push(`State: ${stateStr}`);
        }
      }
    }
    // === LAYOUT (TileSprite, Sprite decorativo, etc.) ===
    else if (obj instanceof Phaser.GameObjects.TileSprite) {
      lines.push(`[${textureName.toUpperCase()}]`);
      lines.push(`Type: LAYOUT (TileSprite)`);

      // Check if it's solid
      if (isStatic) {
        lines.push(`Behaviors: [solid]`);
      }
    }
    // === LAYOUT (Sprite decorativo) ===
    else if (obj instanceof Phaser.GameObjects.Sprite) {
      lines.push(`[${textureName.toUpperCase()}]`);
      lines.push(`Type: LAYOUT (Sprite)`);

      if (isStatic) {
        lines.push(`Behaviors: [solid]`);
      }
    }
    // === OTHER ===
    else {
      lines.push(`[${obj.constructor.name}]`);
      lines.push(`Texture: ${textureName}`);
    }

    // Position and size
    lines.push(`Pos: (${Math.round(body.x)}, ${Math.round(body.y)})`);
    lines.push(`Size: ${Math.round(body.width)}x${Math.round(body.height)}${isStatic ? ' (static)' : ''}`);

    // Velocity for dynamic bodies
    if (!isStatic && 'velocity' in body) {
      const vel = body.velocity;
      if (vel.x !== 0 || vel.y !== 0) {
        lines.push(`Vel: (${Math.round(vel.x)}, ${Math.round(vel.y)})`);
      }
    }

    // Animation info (only for animated sprites)
    const animInfo = (sprite as Phaser.Physics.Arcade.Sprite).anims
      ? this.getAnimationInfo(sprite as Phaser.Physics.Arcade.Sprite)
      : null;
    if (animInfo) {
      lines.push(`Anim: ${animInfo.animKey} [${animInfo.frameIndex}]`);
      lines.push(`Frame: ${animInfo.frameName}`);
      lines.push(`Atlas: ${animInfo.atlasPos}`);
    }

    this.tooltipText.setText(lines.join('\n'));

    // Posicionar tooltip cerca del cursor pero sin salir de la pantalla
    const padding = 15;
    let tooltipX = screenX + padding;
    let tooltipY = screenY + padding;

    // Ajustar si se sale de la pantalla
    if (tooltipX + this.tooltipText.width > this.scene.scale.width) {
      tooltipX = screenX - this.tooltipText.width - padding;
    }
    if (tooltipY + this.tooltipText.height > this.scene.scale.height) {
      tooltipY = screenY - this.tooltipText.height - padding;
    }

    this.tooltipText.setPosition(tooltipX, tooltipY);
    this.tooltipText.setScrollFactor(0);
    this.tooltipText.setVisible(true);
  }

  /**
   * Obtiene información de animación y frame de un sprite
   */
  private getAnimationInfo(sprite: Phaser.Physics.Arcade.Sprite): {
    animKey: string;
    frameIndex: number;
    frameName: string;
    atlasPos: string;
  } | null {
    if (!sprite.anims) return null;

    // Obtener la animación actual
    const currentAnim = sprite.anims.currentAnim;
    const currentFrame = sprite.anims.currentFrame;

    if (!currentAnim && !sprite.frame) {
      return null;
    }

    // Helper para obtener posición en atlas
    const getAtlasPos = (frame: Phaser.Textures.Frame | undefined): string => {
      if (!frame) return 'N/A';
      const data = frame.customData as { x?: number; y?: number } | undefined;
      if (data?.x !== undefined && data?.y !== undefined) {
        return `(${data.x}, ${data.y})`;
      }
      // Fallback: usar cutX/cutY del frame
      return `(${frame.cutX}, ${frame.cutY})`;
    };

    // Si no hay animación activa, mostrar el frame estático
    if (!currentAnim) {
      return {
        animKey: '(static)',
        frameIndex: 0,
        frameName: sprite.frame?.name || 'N/A',
        atlasPos: getAtlasPos(sprite.frame),
      };
    }

    // Obtener índice del frame en la animación
    const frameIndex = currentFrame?.index ?? 0;
    const frame = currentFrame?.frame ?? sprite.frame;
    const frameName = frame?.name ?? 'N/A';

    return {
      animKey: currentAnim.key,
      frameIndex,
      frameName,
      atlasPos: getAtlasPos(frame),
    };
  }

  /**
   * Obtiene estadísticas de assets cargados
   */
  private getAssetStats(): string {
    // Contar texturas (imágenes y atlases)
    let imageCount = 0;
    let atlasCount = 0;
    const textureKeys = this.scene.textures.getTextureKeys();
    for (const key of textureKeys) {
      // Ignorar texturas internas de Phaser
      if (key.startsWith('__') || key === '__DEFAULT' || key === '__MISSING' || key === '__WHITE') continue;
      const texture = this.scene.textures.get(key);
      const frameCount = texture.getFrameNames().length;
      if (frameCount > 1) {
        atlasCount++;
      } else {
        imageCount++;
      }
    }

    // Contar animaciones
    const animCount = this.scene.anims.anims.size;

    // Contar audio
    const audioKeys = this.scene.cache.audio.entries.keys();
    let audioCount = 0;
    for (const _ of audioKeys) {
      audioCount++;
    }

    // Contar JSON
    const jsonKeys = this.scene.cache.json.entries.keys();
    let jsonCount = 0;
    for (const _ of jsonKeys) {
      jsonCount++;
    }

    return `Img:${imageCount} Atlas:${atlasCount} Anim:${animCount} Audio:${audioCount} JSON:${jsonCount}`;
  }

  /**
   * Destruir el overlay
   */
  destroy(): void {
    this.destroyed = true;
    this.scene.input.keyboard!.off('keydown-F1');
    this.scene.input.keyboard!.off('keydown-F6');
    this.scene.input.off('pointerdown', this.onPointerDown, this);
    this.graphics.destroy();
    this.statusText.destroy();
    this.tooltipText.destroy();
    for (const label of this.labelPool) {
      label.destroy();
    }
    this.labelPool = [];
    for (const label of this.imagePointLabelPool) {
      label.destroy();
    }
    this.imagePointLabelPool = [];
    if (this.imagePointLegend) {
      this.imagePointLegend.destroy();
    }
    if (this.imagePointHoverLine) {
      this.imagePointHoverLine.destroy();
    }
  }
}
